import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import { invStatusStyle, methodIcon } from '../data';
import { invoiceDocLabel, invoiceDocDisclaimer, invoiceDocType, invoiceDisplayNumber } from '../utils/invoiceDoc';

const METHODS = ['cash', 'stripe', 'bank', 'online'];
const METHOD_LABELS = { cash: 'Cash', stripe: 'Stripe · Card', bank: 'Bank Transfer', online: 'Online (pre-paid)' };

/* ── PDF print helper ────────────────────────────────────────────────── */
export function printInvoice(inv, cs, rentalsArr = [], vehiclesArr = []) {
  const color  = cs.brandColor || '#1A3A5C';
  const co     = cs.name       || 'Your Company';
  const pf     = parseFloat;

  const subtotal  = pf(inv.subtotal  || 0);
  const vatAmt    = pf(inv.vatAmt ?? inv.vat ?? 0);
  const vatRate   = pf(inv.vatRate   || 19);
  const discount  = pf(inv.discount  || 0);
  const total     = pf(inv.total     || 0);
  const paid      = inv.status === 'paid' ? total : 0;
  const amtDue    = total - paid;
  const vatMode   = inv.vatMode || cs.vatMode || 'inclusive';
  const vatNote   = vatMode === 'inclusive' ? 'Prices include VAT' : 'VAT added on top';

  const logoHtml = cs.logoDataUrl
    ? `<img src="${cs.logoDataUrl}" style="max-height:80px;max-width:180px;object-fit:contain;" />`
    : `<div style="font-size:22px;font-weight:700;color:${color};">${co}</div>`;

  // Build lines — strong fallback to keep the table populated even for legacy invoices
  let lines = (inv.lines || []).filter(l => l && (l.desc || l.total || l.unit));
  if (lines.length === 0) {
    const r = inv.contract ? rentalsArr.find(rr => rr.id === inv.contract) : null;
    const v = r ? vehiclesArr.find(vv => vv.id === r.vid) : null;
    const vehicleName = v?.name || r?.vehicle || inv.vehicle || 'Service';
    const isLease = (r && r.type === 'lease') || inv.isLeaseInvoice;
    const amount = subtotal || total;
    if (isLease) {
      const periodLabel = inv.leaseMonthKey || (inv.issued ? 'billed ' + inv.issued : 'Monthly billing');
      lines = [{ desc: `${vehicleName} · ${periodLabel}`, qty: 1, unit: amount, total: amount }];
    } else if (r) {
      const days = r.calSpan || 1;
      const rate = pf(r.rate || 0);
      lines = [{ desc: `${vehicleName} · ${days} day${days !== 1 ? 's' : ''} rental`, qty: days, unit: rate || amount, total: amount }];
    } else if (amount > 0) {
      lines = [{ desc: vehicleName, qty: 1, unit: amount, total: amount }];
    }
  }

  const linesHtml = lines.map(l => `
    <tr>
      <td style="padding:9px 10px;font-weight:600;">${l.desc || ''}</td>
      <td style="padding:9px 10px;text-align:center;">${l.qty || 1}</td>
      <td style="padding:9px 10px;text-align:right;">${pf(l.unit||0).toFixed(2)}€</td>
      <td style="padding:9px 10px;text-align:right;">0.00%</td>
      <td style="padding:9px 10px;text-align:right;font-weight:600;">${pf(l.total||0).toFixed(2)}€</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>${invoiceDocLabel(inv)} ${inv.num ? invoiceDisplayNumber(inv) : ''}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 13px; color: #333; background: #fff; padding: 30px 40px; }
    .page { max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; }
    .inv-title { font-size: 36px; font-weight: 800; color: ${color}; letter-spacing: 2px; }
    .inv-meta  { margin-top: 6px; font-size: 12px; color: #555; text-align: right; }
    .inv-meta strong { color: #222; }
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 28px; }
    .party-label { font-size: 11px; font-weight: 700; color: ${color}; letter-spacing: 1px; margin-bottom: 6px; border-bottom: 2px solid ${color}; padding-bottom: 3px; }
    .party-name  { font-size: 14px; font-weight: 700; margin-bottom: 4px; }
    .party-info  { font-size: 11px; color: #555; line-height: 1.7; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    thead tr { background: ${color}; color: #fff; }
    thead th { padding: 9px 10px; font-size: 11px; font-weight: 600; text-align: left; letter-spacing: .5px; }
    thead th:not(:first-child) { text-align: center; }
    thead th:last-child { text-align: right; }
    tbody tr { border-bottom: 1px solid #eee; }
    tbody tr:nth-child(even) { background: #f8f9fa; }
    .vat-note { padding: 6px 10px; font-size: 10px; color: #888; background: #f8f9fa; border-top: 1px solid #eee; font-style: italic; }
    .bottom { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-top: 10px; }
    .vat-box h3 { font-size: 11px; font-weight: 700; color: ${color}; letter-spacing: 1px; margin-bottom: 8px; }
    .vat-table { width: 100%; border-collapse: collapse; font-size: 12px; }
    .vat-table th { background: #f0f0f0; padding: 6px 8px; font-size: 10px; font-weight: 600; text-align: right; border: 1px solid #ddd; }
    .vat-table th:first-child { text-align: left; }
    .vat-table td { padding: 6px 8px; border: 1px solid #ddd; text-align: right; }
    .vat-table td:first-child { text-align: left; }
    .totals { font-size: 12px; }
    .totals-row { display: flex; justify-content: space-between; padding: 4px 0; color: #555; border-bottom: 1px solid #eee; }
    .totals-row.total-final { font-size: 15px; font-weight: 700; color: ${color}; border-top: 2px solid ${color}; border-bottom: none; padding-top: 8px; margin-top: 4px; }
    .payment-section { margin-top: 20px; }
    .payment-section h3 { font-size: 11px; font-weight: 700; color: ${color}; letter-spacing: 1px; margin-bottom: 8px; text-align: center; }
    .payment-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; border: 1px solid #ddd; border-radius: 4px; overflow: hidden; font-size: 12px; }
    .payment-cell { padding: 10px 14px; text-align: center; border-right: 1px solid #ddd; }
    .payment-cell:last-child { border-right: none; }
    .payment-cell .plabel { font-size: 10px; color: #888; margin-bottom: 4px; }
    .payment-cell .pvalue { font-weight: 700; font-size: 14px; }
    .payment-cell.due .pvalue { color: ${color}; }
    .terms { margin-top: 20px; background: #f8f9fa; border: 1px solid #e0e0e0; border-radius: 4px; padding: 12px 16px; }
    .terms h3 { font-size: 11px; font-weight: 700; letter-spacing: 1px; margin-bottom: 8px; color: #444; }
    .terms p  { font-size: 11px; color: #555; line-height: 1.7; }
    .terms .highlight { color: ${color}; }
    .footer { margin-top: 24px; text-align: center; font-size: 10px; color: #aaa; border-top: 1px solid #eee; padding-top: 10px; }
    @media print { body { padding: 0; } @page { margin: 15mm; } }
  </style>
</head>
<body>
<div class="page">
  <div class="header">
    <div>${logoHtml}</div>
    <div style="text-align:right;">
      <div class="inv-title">${invoiceDocLabel(inv)}</div>
      <div class="inv-meta">
        ${inv.num ? `<div>NO. <strong>${invoiceDisplayNumber(inv)}</strong></div>` : ''}
        <div>DATE: <strong>${inv.issued || inv.issueDate || ''}</strong></div>
        ${inv.due ? `<div>DUE: <strong>${inv.due}</strong></div>` : ''}
        ${inv.contract ? `<div>REF: <strong>${inv.contract}</strong></div>` : ''}
      </div>
    </div>
  </div>
  <div class="parties">
    <div>
      <div class="party-label">FROM</div>
      <div class="party-name">${co}</div>
      <div class="party-info">
        ${cs.vatNum  ? `V.A.T ${cs.vatNum}<br/>` : ''}
        ${cs.regNum  ? `Reg. ${cs.regNum}<br/>` : ''}
        ${cs.address ? `${cs.address}<br/>` : ''}
        ${cs.phone   ? `${cs.phone}<br/>` : ''}
        ${cs.email   ? cs.email : ''}
        ${cs.website ? `<br/>${cs.website}` : ''}
      </div>
    </div>
    <div>
      <div class="party-label">CLIENT</div>
      <div class="party-name">${inv.customer || ''}</div>
      <div class="party-info">
        ${inv.customerCompany && inv.customerCompany !== inv.customer ? `${inv.customerCompany}<br/>` : ''}
        ${inv.customerVatNumber ? `V.A.T ${inv.customerVatNumber}<br/>` : ''}
        ${inv.email   ? `${inv.email}<br/>` : ''}
        ${inv.addr    ? `${inv.addr}<br/>` : ''}
        ${inv.vehicle ? `Vehicle: ${inv.vehicle}` : ''}
      </div>
    </div>
  </div>
  <table>
    <thead><tr>
      <th style="text-align:left;width:45%">Item</th>
      <th style="text-align:center;width:8%">Qty</th>
      <th style="text-align:right;width:15%">Unit price</th>
      <th style="text-align:right;width:15%">Unit discount</th>
      <th style="text-align:right;width:17%">Net total</th>
    </tr></thead>
    <tbody>${linesHtml}</tbody>
    <tfoot>
      <tr><td colspan="5" class="vat-note">
        ${vatMode === 'exclusive'
          ? `* All amounts above are ex-VAT. ${vatRate}% VAT is added separately in the totals below.`
          : `* Line amounts shown ex-VAT for transparency. ${vatRate}% VAT breakdown shown below. Customer paid the gross "Total" figure.`}
      </td></tr>
    </tfoot>
  </table>
  <div class="bottom">
    <div class="vat-box">
      <h3>VAT ANALYSIS</h3>
      <table class="vat-table">
        <thead><tr><th>VAT</th><th>Net amount</th><th>VAT amount</th></tr></thead>
        <tbody><tr><td>${vatRate.toFixed(2)}%</td><td>${subtotal.toFixed(2)}€</td><td>${vatAmt.toFixed(2)}€</td></tr></tbody>
      </table>
    </div>
    <div class="totals">
      <div class="totals-row"><span>Net total:</span><span>${subtotal.toFixed(2)}€</span></div>
      ${discount > 0 ? `<div class="totals-row"><span>Discount:</span><span>-${discount.toFixed(2)}€</span></div>` : ''}
      <div class="totals-row"><span>VAT (${vatRate}%):</span><span>${vatAmt.toFixed(2)}€</span></div>
      <div class="totals-row total-final"><span>Total:</span><span>${total.toFixed(2)}€</span></div>
    </div>
  </div>
  <div class="payment-section">
    <h3>PAYMENT INFORMATION</h3>
    <div class="payment-grid">
      <div class="payment-cell"><div class="plabel">Paid amount</div><div class="pvalue">${paid.toFixed(2)}€</div></div>
      <div class="payment-cell due"><div class="plabel">Amount due</div><div class="pvalue">${amtDue.toFixed(2)}€</div></div>
      <div class="payment-cell due"><div class="plabel">Client amount due</div><div class="pvalue">${amtDue.toFixed(2)}€</div></div>
    </div>
  </div>
  ${cs.bankName || cs.iban || cs.swift || cs.bankDetails || cs.invTerms || cs.invFooter || cs.invPaymentTerms ? `
  <div class="terms">
    <h3>TERMS &amp; CONDITIONS</h3>
    <p>
      ${cs.invPaymentTerms ? `${cs.invPaymentTerms}<br/><br/>` : ''}
      ${(cs.bankName || cs.iban || cs.swift || cs.bankAccountName) ? `
        <strong>Bank details:</strong><br/>
        ${cs.bankName        ? `${cs.bankName}<br/>` : ''}
        ${cs.bankAccountName ? `Account: ${cs.bankAccountName}<br/>` : ''}
        ${cs.iban            ? `<span class="highlight">IBAN: ${cs.iban}</span><br/>` : ''}
        ${cs.swift           ? `<span class="highlight">SWIFT (BIC): ${cs.swift}</span><br/>` : ''}
        <br/>
      ` : ''}
      ${cs.bankDetails ? `<span class="highlight">${cs.bankDetails.replace(/\n/g,'<br/>')}</span><br/>` : ''}
      ${cs.invTerms    ? `<br/>${cs.invTerms.replace(/\n/g,'<br/>')}` : ''}
      ${cs.invFooter   ? `<br/><br/><em>${cs.invFooter}</em>` : ''}
    </p>
  </div>` : ''}
  ${invoiceDocDisclaimer(inv) ? `<div style="margin-top:18px;padding:10px 14px;background:#f8f9fa;border:1px solid #e0e0e0;border-radius:4px;font-size:10px;color:#777;line-height:1.6;font-style:italic;">${invoiceDocDisclaimer(inv)}</div>` : ''}
  <div class="footer">Generated by FleetRent CRM &nbsp;·&nbsp; ${new Date().toLocaleDateString('en-GB')}</div>
</div>
<script>window.onload = function(){ window.print(); }</script>
</body>
</html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

/* ── Blank line helper ──────────────────────────────────────────────── */
function blankLine() { return { _id: Math.random(), desc: '', qty: 1, unit: 0, total: 0 }; }

/* ─────────────────────────────────────────────────────────────────────── */

export default function InvoiceViewModal() {
  const { modals, closeModal, invoices, updateInvoice, updateInvoiceStatus, issueInvoice, companySettings, showToast, rentals, vehicles } = useApp();
  const open  = modals['view-invoice'];
  const invId = modals['view-invoice-id'];
  const inv   = invId ? invoices.find(i => i.id === invId) : null;

  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [editVatMode, setEditVatMode] = useState('inclusive');

  // Reset edit state when modal opens/closes or invoice changes
  useEffect(() => {
    setEditing(false);
    setEditData(null);
    setEditLines([]);
  }, [open, invId]);

  if (!open || !inv) return null;

  const st  = invStatusStyle[inv.status] || {};
  const cs  = companySettings || {};
  const col = cs.brandColor || 'var(--ac)';

  const canEdit = inv.status === 'draft' || inv.status === 'issued' || inv.status === 'pending';
  const isPaid  = inv.status === 'paid';

  function startEdit() {
    setEditData({
      customer: inv.customer || '',
      email: inv.email || '',
      addr: inv.addr || '',
      vehicle: inv.vehicle || '',
      vid: inv.vid || '',
      contract: inv.contract || '',
      method: inv.method || 'cash',
      issueDate: inv.issueDate || '',
      dueDate: inv.dueDate || '',
      vatRate: inv.vatRate || 19,
      discount: inv.discount || 0,
      discType: inv.discType || 'eur',
      notes: inv.notes || '',
    });
    setEditLines((inv.lines || []).map(l => ({ ...l, _id: l._id || Math.random() })));
    setEditVatMode(inv.vatMode || cs.vatMode || 'inclusive');
    setEditing(true);
  }

  function saveEdit() {
    if (!editData.customer.trim()) { alert('Customer name is required'); return; }
    const subtotal = editLines.reduce((s, l) => s + Number(l.total), 0);
    const discAmt = editData.discType === '%' ? subtotal * (Number(editData.discount) / 100) : Number(editData.discount);
    const afterDisc = subtotal - discAmt;
    const vatAmt = editVatMode === 'inclusive'
      ? afterDisc - afterDisc / (1 + Number(editData.vatRate) / 100)
      : afterDisc * Number(editData.vatRate) / 100;
    const total = editVatMode === 'inclusive' ? afterDisc : afterDisc + vatAmt;

    const fmtDate = (iso) => {
      if (!iso) return '';
      return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    updateInvoice(inv.id, {
      customer: editData.customer,
      email: editData.email,
      addr: editData.addr,
      vehicle: editData.vehicle,
      vid: editData.vid,
      contract: editData.contract,
      method: editData.method,
      methodLabel: { cash: 'Cash', stripe: 'Stripe · Card', bank: 'Bank Transfer', online: 'Online (pre-paid)' }[editData.method] || editData.method,
      issueDate: editData.issueDate,
      dueDate: editData.dueDate,
      issue: fmtDate(editData.issueDate),
      due: fmtDate(editData.dueDate),
      vatRate: Number(editData.vatRate),
      discount: Number(editData.discount),
      discType: editData.discType,
      notes: editData.notes,
      lines: editLines.map(l => ({ desc: l.desc, qty: Number(l.qty), unit: Number(l.unit), total: Number(l.total) })),
      subtotal, vatAmt, total, vatMode: editVatMode,
    });
    showToast('✅ Pro forma updated');
    setEditing(false);
  }

  function updEdit(k, v) { setEditData(p => ({ ...p, [k]: v })); }
  function updEditLine(i, k, v) {
    setEditLines(prev => prev.map((l, idx) => {
      if (idx !== i) return l;
      const updated = { ...l, [k]: v };
      if (k === 'qty' || k === 'unit') updated.total = Number(updated.qty) * Number(updated.unit);
      return updated;
    }));
  }
  function addEditLine() { setEditLines(prev => [...prev, blankLine()]); }
  function removeEditLine(i) { setEditLines(prev => prev.filter((_, idx) => idx !== i)); }

  function handleIssue() {
    issueInvoice(inv.id);
    showToast('📄 Pro forma issued');
    closeModal('view-invoice');
  }

  function handleMarkPaid() {
    updateInvoiceStatus(inv.id, 'paid');
    showToast('✅ Pro forma marked as paid');
    closeModal('view-invoice');
  }

  function handleResendEmail() {
    // The actual sending happens in the back-end via the developer's mail service.
    // From the front-end we mark the invoice as sent (re-issue notification) and surface a toast.
    // In production: this triggers a POST /api/invoices/:id/resend which fires the
    // appropriate email template (invoice_issued or payment_reminder if overdue).
    const recipient = inv.email || inv.customerEmail || '(no email on file)';
    const isOverdue = inv.status === 'overdue';
    const templateName = isOverdue ? 'Payment reminder' : 'Pro forma';
    // Stamp the invoice with a lastSentAt timestamp so the operator can track
    if (updateInvoice) {
      updateInvoice(inv.id, { lastSentAt: new Date().toISOString(), lastSentTo: recipient });
    }
    showToast(`📧 ${templateName} email queued · ${recipient}`);
  }

  const fmt = (n) => Number(n).toFixed(2);

  // Derived numbers for view mode (mirror the print logic)
  const vSubtotal = parseFloat(inv.subtotal || 0);
  const vVatAmt   = parseFloat(inv.vatAmt ?? inv.vat ?? 0);
  const vVatRate  = parseFloat(inv.vatRate || 19);
  const vDiscount = parseFloat(inv.discount || 0);
  const vTotal    = parseFloat(inv.total || 0);
  const vPaid     = inv.status === 'paid' ? vTotal : 0;
  const vAmtDue   = vTotal - vPaid;

  /* ── EDIT MODE ── */
  if (editing && editData) {
    const subtotal = editLines.reduce((s, l) => s + Number(l.total), 0);
    const discAmt = editData.discType === '%' ? subtotal * (Number(editData.discount) / 100) : Number(editData.discount);
    const afterDisc = subtotal - discAmt;
    const vatAmt = editVatMode === 'inclusive'
      ? afterDisc - afterDisc / (1 + Number(editData.vatRate) / 100)
      : afterDisc * Number(editData.vatRate) / 100;
    const total = editVatMode === 'inclusive' ? afterDisc : afterDisc + vatAmt;

    return (
      <Modal open={!!open} size="lg" onClose={() => closeModal('view-invoice')}>
        <ModalHeader
          title={invoiceDisplayNumber(inv)}
          subtitle="Edit Pro Forma"
          onClose={() => closeModal('view-invoice')}
          extra={<span style={{ fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 10, background: '#e3f2fd', color: '#1565c0' }}>✏️ Editing</span>}
        />
        <ModalBody>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div className="fg">
                <label className="fl">Customer *</label>
                <input className="fi" value={editData.customer} onChange={e => updEdit('customer', e.target.value)} />
              </div>
              <div className="fg">
                <label className="fl">Email</label>
                <input className="fi" value={editData.email} onChange={e => updEdit('email', e.target.value)} />
              </div>
              <div className="fg">
                <label className="fl">Address</label>
                <input className="fi" value={editData.addr} onChange={e => updEdit('addr', e.target.value)} />
              </div>
            </div>
            <div>
              <div className="fg">
                <label className="fl">Vehicle</label>
                <input className="fi" value={editData.vehicle} onChange={e => updEdit('vehicle', e.target.value)} />
              </div>
              <div className="fg">
                <label className="fl">Contract / Ref</label>
                <input className="fi" value={editData.contract} onChange={e => updEdit('contract', e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div className="fg">
                  <label className="fl">Issue Date</label>
                  <input className="fi" type="date" value={editData.issueDate} onChange={e => updEdit('issueDate', e.target.value)} />
                </div>
                <div className="fg">
                  <label className="fl">Due Date</label>
                  <input className="fi" type="date" value={editData.dueDate} onChange={e => updEdit('dueDate', e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx2)' }}>Line Items</div>
              <button className="btn btn-ghost btn-sm" onClick={addEditLine}>+ Add Line</button>
            </div>
            <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 60px 80px 80px 28px', background: 'var(--sur2)', padding: '5px 8px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)' }}>
                <div>Description</div><div>Qty</div><div>Unit €</div><div>Total €</div><div></div>
              </div>
              {editLines.map((l, i) => (
                <div key={l._id} style={{ display: 'grid', gridTemplateColumns: '3fr 60px 80px 80px 28px', borderTop: '1px solid var(--bd)', padding: '3px 8px', alignItems: 'center' }}>
                  <input className="fi" style={{ border: 'none', background: 'transparent', padding: '3px 0' }}
                    value={l.desc} onChange={e => updEditLine(i, 'desc', e.target.value)} placeholder="Item description" />
                  <input className="fi" style={{ border: 'none', background: 'transparent', padding: '3px 0', textAlign: 'right' }}
                    type="number" min="1" value={l.qty} onChange={e => updEditLine(i, 'qty', e.target.value)} />
                  <input className="fi" style={{ border: 'none', background: 'transparent', padding: '3px 0', textAlign: 'right' }}
                    type="number" min="0" step="0.01" value={l.unit} onChange={e => updEditLine(i, 'unit', e.target.value)} />
                  <div style={{ fontSize: 12, textAlign: 'right', fontWeight: 500 }}>€{fmt(l.total)}</div>
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ac)', fontSize: 14, padding: 2 }}
                    onClick={() => removeEditLine(i)} disabled={editLines.length === 1}>×</button>
                </div>
              ))}
            </div>
          </div>

          {/* Totals + settings */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
            <div>
              <div className="fg">
                <label className="fl">Payment Method</label>
                <select className="fi" value={editData.method} onChange={e => updEdit('method', e.target.value)}>
                  {METHODS.map(m => <option key={m} value={m}>{METHOD_LABELS[m]}</option>)}
                </select>
              </div>
              <div className="fg">
                <label className="fl">Notes</label>
                <textarea className="fi" rows={2} style={{ resize: 'vertical' }} value={editData.notes} onChange={e => updEdit('notes', e.target.value)} />
              </div>
            </div>
            <div style={{ background: 'var(--sur2)', borderRadius: 'var(--r)', border: '1px solid var(--bd)', padding: 12, fontSize: 12, alignSelf: 'start' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>Subtotal</span>
                <span>€{fmt(subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>Discount</span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input type="number" min="0" step="0.01" className="fi" style={{ width: 60, padding: '2px 5px', fontSize: 11 }}
                    value={editData.discount} onChange={e => updEdit('discount', e.target.value)} />
                  <select className="fi" style={{ width: 52, padding: '2px 4px', fontSize: 11 }}
                    value={editData.discType} onChange={e => updEdit('discType', e.target.value)}>
                    <option value="eur">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 4 }}>
                <span style={{ color: 'var(--tx3)' }}>VAT (%)</span>
                <input type="number" min="0" max="100" className="fi" style={{ width: 60, padding: '2px 5px', fontSize: 11 }}
                  value={editData.vatRate} onChange={e => updEdit('vatRate', e.target.value)} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ color: 'var(--tx3)', fontSize: 11 }}>VAT Mode</span>
                <div style={{ display: 'flex', gap: 0, border: '1px solid var(--bd)', borderRadius: 5, overflow: 'hidden' }}>
                  <button type="button" onClick={() => setEditVatMode('inclusive')}
                    style={{ padding: '2px 8px', fontSize: 10, fontWeight: 600, border: 'none', cursor: 'pointer',
                      background: editVatMode === 'inclusive' ? 'var(--ac)' : 'var(--sur)', color: editVatMode === 'inclusive' ? '#fff' : 'var(--tx3)' }}>
                    Inclusive
                  </button>
                  <button type="button" onClick={() => setEditVatMode('exclusive')}
                    style={{ padding: '2px 8px', fontSize: 10, fontWeight: 600, border: 'none', borderLeft: '1px solid var(--bd)', cursor: 'pointer',
                      background: editVatMode === 'exclusive' ? 'var(--ac)' : 'var(--sur)', color: editVatMode === 'exclusive' ? '#fff' : 'var(--tx3)' }}>
                    Exclusive
                  </button>
                </div>
              </div>
              <div style={{ fontSize: 9, color: 'var(--tx3)', marginBottom: 4 }}>
                {editVatMode === 'inclusive' ? 'Prices include VAT' : 'VAT added on top of prices'}
              </div>
              <div style={{ borderTop: '1px solid var(--bd)', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13 }}>
                <span>Total</span>
                <span style={{ color: 'var(--ac)' }}>€{fmt(total)}</span>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveEdit}>💾 Save Changes</button>
        </ModalFooter>
      </Modal>
    );
  }

  /* ── Build line items (with safe fallback) ─────────────────────────── */
  let displayLines = [];
  try {
    displayLines = (inv.lines || []).filter(l => l && (l.desc || l.total || l.unit));
  } catch (e) { displayLines = []; }
  if (displayLines.length === 0) {
    try {
      const r = inv.contract && rentals ? rentals.find(rr => rr.id === inv.contract) : null;
      const v = r && vehicles ? vehicles.find(vv => vv.id === r.vid) : null;
      const vehicleName = v?.name || r?.vehicle || inv.vehicle || 'Service';
      const isLease = (r && r.type === 'lease') || inv.isLeaseInvoice;
      const subTot = parseFloat(inv.subtotal || 0);
      const totAmt = parseFloat(inv.total || 0);
      const amount = subTot || totAmt;
      if (isLease) {
        const periodLabel = inv.leaseMonthKey || (inv.issued ? 'billed ' + inv.issued : 'Monthly billing');
        displayLines = [{ desc: `${vehicleName} · ${periodLabel}`, qty: 1, unit: amount, total: amount }];
      } else if (r) {
        const days = r.calSpan || 1;
        const rate = parseFloat(r.rate || 0);
        displayLines = [{ desc: `${vehicleName} · ${days} day${days !== 1 ? 's' : ''} rental`, qty: days, unit: rate || amount, total: amount }];
      } else if (amount > 0) {
        displayLines = [{ desc: vehicleName, qty: 1, unit: amount, total: amount }];
      } else {
        // Absolute last resort — show something
        displayLines = [{ desc: inv.vehicle || inv.customer || 'Pro forma item', qty: 1, unit: 0, total: 0 }];
      }
    } catch (e) {
      displayLines = [{ desc: inv.vehicle || 'Pro forma item', qty: 1, unit: 0, total: 0 }];
    }
  }

  /* ── VIEW MODE ── */
  return (
    <Modal open={!!open} size="lg" onClose={() => closeModal('view-invoice')}>
      <ModalHeader
        title={inv.num || 'DRAFT'}
        subtitle={inv.customer}
        onClose={() => closeModal('view-invoice')}
        extra={
          <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 10, background: st.bg, color: st.tx, border: `1px solid ${st.bd}` }}>
            {invStatusStyle[inv.status]?.label || inv.status}
          </span>
        }
      />
      <ModalBody>

        {/* Lock badge for paid invoices */}
        {isPaid && (
          <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: 'var(--gr)', fontWeight: 600 }}>
            🔒 This pro forma is paid and cannot be edited.
          </div>
        )}

        {/* Draft notice */}
        {inv.status === 'draft' && (
          <div style={{ background: '#f0f0f0', border: '1px solid #ccc', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#666', fontWeight: 600 }}>
            📝 This is a draft — no number assigned yet. Issue it to assign a number.
          </div>
        )}

        {/* Document-type notice — pro forma vs receipt */}
        {inv.status !== 'draft' && (() => {
          const dt = invoiceDocType(inv);
          const isReceipt = dt === 'receipt';
          return (
            <div style={{
              background: isReceipt ? 'var(--gr-l)' : 'var(--am-l)',
              border: `1px solid ${isReceipt ? 'var(--gr-b)' : 'var(--am-b)'}`,
              borderRadius: 8, padding: '8px 12px', marginBottom: 12,
              fontSize: 11, color: isReceipt ? 'var(--gr)' : 'var(--am)', lineHeight: 1.5,
            }}>
              {isReceipt
                ? <>🧾 <strong>Payment Receipt</strong> — paid online. The payment provider issued the official receipt to the customer by email.</>
                : <>📋 <strong>Pro Forma</strong> — not an official tax invoice. The official receipt is issued on payment (at the desk for cash, or by accounts for bank transfer). Mark it paid here once settled.</>}
            </div>
          );
        })()}

        {/* ── HEADER: Logo + INVOICE title row (mirrors print) ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            {cs.logoDataUrl
              ? <img src={cs.logoDataUrl} alt="logo" style={{ maxHeight: 70, maxWidth: 180, objectFit: 'contain', display: 'block' }} />
              : <div style={{ fontSize: 20, fontWeight: 800, color: col }}>{cs.name || 'Your Company'}</div>
            }
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: col, letterSpacing: 1.5 }}>
              {invoiceDocLabel(inv)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 6, lineHeight: 1.7 }}>
              {inv.num && <div>NO. <strong style={{ color: 'var(--tx)' }}>{invoiceDisplayNumber(inv)}</strong></div>}
              {(inv.issue || inv.issueDate) && <div>DATE: <strong style={{ color: 'var(--tx)' }}>{inv.issueDate || inv.issue}</strong></div>}
              {inv.due && <div>DUE: <strong style={{ color: 'var(--tx)' }}>{inv.due}</strong></div>}
              {inv.contract && <div>REF: <strong style={{ color: 'var(--tx)', fontFamily: "'DM Mono',monospace" }}>{inv.contract}</strong></div>}
            </div>
          </div>
        </div>

        {/* ── FROM / CLIENT (mirrors print) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: col, letterSpacing: 1, marginBottom: 6, paddingBottom: 3, borderBottom: `2px solid ${col}`, textTransform: 'uppercase' }}>From</div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{cs.name || 'Your Company'}</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)', lineHeight: 1.7 }}>
              {cs.vatNum  && <div>V.A.T {cs.vatNum}</div>}
              {cs.regNum  && <div>Reg. {cs.regNum}</div>}
              {cs.address && <div>{cs.address}</div>}
              {cs.phone   && <div>{cs.phone}</div>}
              {cs.email   && <div>{cs.email}</div>}
              {cs.website && <div>{cs.website}</div>}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: col, letterSpacing: 1, marginBottom: 6, paddingBottom: 3, borderBottom: `2px solid ${col}`, textTransform: 'uppercase' }}>Client</div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{inv.customer}</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)', lineHeight: 1.7 }}>
              {inv.customerCompany && inv.customerCompany !== inv.customer && <div>{inv.customerCompany}</div>}
              {inv.customerVatNumber && <div>V.A.T {inv.customerVatNumber}</div>}
              {inv.email   && <div>{inv.email}</div>}
              {inv.addr    && <div>{inv.addr}</div>}
              {inv.vehicle && <div>Vehicle: {inv.vehicle}</div>}
            </div>
          </div>
        </div>

        {/* ── LINE ITEMS TABLE — using table HTML (more reliable than grid) ── */}
        <table style={{
          width: '100%', borderCollapse: 'collapse', marginBottom: 20,
          border: '1px solid #ccc', tableLayout: 'fixed',
        }}>
          <thead>
            <tr style={{ background: col || '#1A3A5C', color: '#ffffff' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left',  fontSize: 11, fontWeight: 600, width: '45%' }}>Item</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 11, fontWeight: 600, width: '8%' }}>Qty</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 600, width: '15%' }}>Unit price</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 600, width: '15%' }}>Unit discount</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 600, width: '17%' }}>Net total</th>
            </tr>
          </thead>
          <tbody>
            {displayLines.map((line, i) => (
              <tr key={i} style={{ background: i % 2 === 1 ? '#f8f9fa' : '#ffffff', borderTop: '1px solid #eee' }}>
                <td style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600 }}>{line.desc}</td>
                <td style={{ padding: '10px 12px', fontSize: 12, textAlign: 'center' }}>{line.qty ?? 1}</td>
                <td style={{ padding: '10px 12px', fontSize: 12, textAlign: 'right' }}>{parseFloat(line.unit || 0).toFixed(2)}€</td>
                <td style={{ padding: '10px 12px', fontSize: 12, textAlign: 'right' }}>0.00%</td>
                <td style={{ padding: '10px 12px', fontSize: 12, textAlign: 'right', fontWeight: 600 }}>{parseFloat(line.total || 0).toFixed(2)}€</td>
              </tr>
            ))}
            <tr>
              <td colSpan={5} style={{ padding: '6px 12px', fontSize: 10, color: '#888', background: '#f8f9fa', borderTop: '1px solid #eee', fontStyle: 'italic' }}>
                {(inv.vatMode || cs.vatMode) === 'exclusive'
                  ? `* All amounts above are ex-VAT. ${vVatRate}% VAT is added separately in the totals below.`
                  : `* Line amounts shown ex-VAT for transparency. ${vVatRate}% VAT breakdown shown below. Customer paid the gross "Total" figure.`}
              </td>
            </tr>
          </tbody>
        </table>

        {/* ── BOTTOM: VAT Analysis (left) + Totals (right) — mirrors print ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: col, letterSpacing: 1, marginBottom: 8, textTransform: 'uppercase' }}>VAT Analysis</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ background: '#f0f0f0', padding: '6px 8px', fontSize: 10, fontWeight: 600, textAlign: 'left', border: '1px solid #ddd' }}>VAT</th>
                  <th style={{ background: '#f0f0f0', padding: '6px 8px', fontSize: 10, fontWeight: 600, textAlign: 'right', border: '1px solid #ddd' }}>Net amount</th>
                  <th style={{ background: '#f0f0f0', padding: '6px 8px', fontSize: 10, fontWeight: 600, textAlign: 'right', border: '1px solid #ddd' }}>VAT amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'left' }}>{vVatRate.toFixed(2)}%</td>
                  <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>{vSubtotal.toFixed(2)}€</td>
                  <td style={{ padding: '6px 8px', border: '1px solid #ddd', textAlign: 'right' }}>{vVatAmt.toFixed(2)}€</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: 'var(--tx3)', borderBottom: '1px solid #eee' }}>
              <span>Net total:</span><span>{vSubtotal.toFixed(2)}€</span>
            </div>
            {vDiscount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: 'var(--tx3)', borderBottom: '1px solid #eee' }}>
                <span>Discount:</span><span>-{vDiscount.toFixed(2)}€</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: 'var(--tx3)', borderBottom: '1px solid #eee' }}>
              <span>VAT ({vVatRate}%):</span><span>{vVatAmt.toFixed(2)}€</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', marginTop: 4, fontSize: 15, fontWeight: 700, color: col, borderTop: `2px solid ${col}` }}>
              <span>Total:</span><span>{vTotal.toFixed(2)}€</span>
            </div>
          </div>
        </div>

        {/* ── PAYMENT INFORMATION (mirrors print) ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: col, letterSpacing: 1, marginBottom: 8, textAlign: 'center', textTransform: 'uppercase' }}>Payment Information</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', border: '1px solid #ddd', borderRadius: 4, overflow: 'hidden', fontSize: 12 }}>
            <div style={{ padding: '10px 14px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
              <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Paid amount</div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{vPaid.toFixed(2)}€</div>
            </div>
            <div style={{ padding: '10px 14px', textAlign: 'center', borderRight: '1px solid #ddd' }}>
              <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Amount due</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: col }}>{vAmtDue.toFixed(2)}€</div>
            </div>
            <div style={{ padding: '10px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Client amount due</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: col }}>{vAmtDue.toFixed(2)}€</div>
            </div>
          </div>
          {/* Payment method + notes — small caption */}
          <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', fontSize: 11 }}>
            <div style={{ color: 'var(--tx3)' }}>Payment method</div>
            <div style={{ fontWeight: 500 }}>{methodIcon[inv.method] || ''} {inv.methodLabel || inv.method}</div>
            {inv.notes && <div style={{ marginTop: 6, color: 'var(--tx2)', fontStyle: 'italic' }}>{inv.notes}</div>}
          </div>
        </div>

        {/* ── TERMS & CONDITIONS (mirrors print, only if any data) ── */}
        {(cs.bankName || cs.iban || cs.swift || cs.bankDetails || cs.invTerms || cs.invFooter || cs.invPaymentTerms) && (
          <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 4, padding: '12px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 8, color: 'var(--tx2)', textTransform: 'uppercase' }}>Terms &amp; Conditions</div>
            <div style={{ fontSize: 11, color: 'var(--tx3)', lineHeight: 1.7 }}>
              {cs.invPaymentTerms && <div style={{ marginBottom: 8 }}>{cs.invPaymentTerms}</div>}
              {(cs.bankName || cs.iban || cs.swift || cs.bankAccountName) && (
                <div style={{ marginBottom: 8 }}>
                  <strong>Bank details:</strong>
                  {cs.bankName        && <div>{cs.bankName}</div>}
                  {cs.bankAccountName && <div>Account: {cs.bankAccountName}</div>}
                  {cs.iban            && <div style={{ color: col }}>IBAN: {cs.iban}</div>}
                  {cs.swift           && <div style={{ color: col }}>SWIFT (BIC): {cs.swift}</div>}
                </div>
              )}
              {cs.bankDetails && <div style={{ color: col, whiteSpace: 'pre-line', marginBottom: 8 }}>{cs.bankDetails}</div>}
              {cs.invTerms    && <div style={{ whiteSpace: 'pre-line' }}>{cs.invTerms}</div>}
              {cs.invFooter   && <div style={{ marginTop: 8, fontStyle: 'italic' }}>{cs.invFooter}</div>}
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <button className="btn btn-ghost" onClick={() => closeModal('view-invoice')}>Close</button>

        {/* Edit button — only for draft/issued/pending */}
        {canEdit && (
          <button className="btn btn-ghost" onClick={startEdit}>✏️ Edit</button>
        )}

        {/* Issue — only for drafts */}
        {inv.status === 'draft' && (
          <button className="btn btn-primary" onClick={handleIssue}>📄 Issue Pro Forma</button>
        )}

        {/* Mark as Paid — for issued/pending/overdue */}
        {(inv.status === 'issued' || inv.status === 'pending' || inv.status === 'overdue') && (
          <button className="btn btn-green" onClick={handleMarkPaid}>✓ Mark as Paid</button>
        )}

        {/* Resend / Send by email — only if invoice has a number and a recipient */}
        {inv.num && (inv.email || inv.customerEmail) && (
          <button
            className="btn btn-ghost"
            onClick={handleResendEmail}
            title={inv.lastSentAt ? `Last sent ${new Date(inv.lastSentAt).toLocaleString('en-GB')}` : 'Email this invoice to the customer'}
          >
            📧 {inv.lastSentAt ? 'Resend Email' : 'Send Email'}
          </button>
        )}

        {/* Print — only if invoice has a number */}
        {inv.num && (
          <button className="btn btn-ghost" onClick={() => printInvoice(inv, cs, rentals, vehicles)}>🖨️ Print / PDF</button>
        )}
      </ModalFooter>
    </Modal>
  );
}
